import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, IconButton, Stack, Button, CircularProgress,
  Divider, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Alert, Select, MenuItem, FormControl, Collapse,
  useMediaQuery, Menu,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import { visit } from 'unist-util-visit';
import api from '../api';

// ─── remark plugin: turn directive nodes into hast elements ───────────────────
function remarkDirectiveRenderer() {
  return (tree) => {
    visit(tree, (node) => {
      if (node.type === 'containerDirective' || node.type === 'leafDirective' || node.type === 'textDirective') {
        const data = node.data || (node.data = {});
        data.hName = 'div';
        data.hProperties = {
          ...node.attributes,
          'data-directive': node.name,
        };
      }
    });
  };
}

const PROVIDERS = {
  openai:    { label: 'OpenAI',         color: '#10a37f', models: ['gpt-4o', 'gpt-4o-mini'] },
  anthropic: { label: 'Anthropic',      color: '#d4a574', models: ['claude-opus-4-6', 'claude-sonnet-4-6'] },
  gemini:    { label: 'Google Gemini',  color: '#4285F4', models: ['gemini-2.0-flash', 'gemini-1.5-pro'] },
  bedrock:   { label: 'Amazon Bedrock', color: '#FF9900', models: ['global.anthropic.claude-sonnet-4-5-20250929-v1:0'] },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-SG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// ─── Shared markdown sx styles ────────────────────────────────────────────────
const mdSx = {
  '& p':  { mb: 1.5, color: 'var(--tx-write)', lineHeight: 1.8, mt: 0 },
  '& ul, & ol': { pl: 3, mb: 1.5, color: 'var(--tx-write)' },
  '& li': { mb: 0.5, lineHeight: 1.7 },
  '& blockquote': { borderLeft: '3px solid #6366f1', pl: 2, my: 1.5, color: 'var(--tx-lo)', fontStyle: 'italic' },
  '& code': { bgcolor: 'var(--bg-card)', px: 0.7, py: 0.2, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.85em', color: '#6366f1' },
  '& pre': { bgcolor: 'var(--bg-card)', p: 2, borderRadius: 2, overflowX: 'auto', mb: 1.5 },
  '& pre code': { bgcolor: 'transparent', p: 0, color: 'var(--tx-write)' },
  '& hr': { border: 'none', borderTop: '1px solid var(--bd-lo)', my: 2 },
  '& strong': { color: 'var(--tx-write)', fontWeight: 700 },
  '& em': { color: 'var(--tx-write)', fontStyle: 'italic' },
  '& a': { color: '#6366f1' },
  '& table': { borderCollapse: 'collapse', width: '100%', mb: 2 },
  '& th': { bgcolor: 'var(--bg-card)', color: 'var(--tx-write)', fontWeight: 600, px: 1.5, py: 0.8, textAlign: 'left', border: '1px solid var(--bd-md)', fontSize: '0.85rem' },
  '& td': { color: 'var(--tx-write)', px: 1.5, py: 0.7, border: '1px solid var(--bd-lo)', fontSize: '0.85rem' },
  '& tr:nth-of-type(even) td': { bgcolor: 'var(--bg-low)' },
  '& input[type="checkbox"]': { accentColor: '#6366f1', mr: 0.5 },
  '& img': { maxWidth: '100%', maxHeight: 480, objectFit: 'contain', borderRadius: 2, display: 'block', my: 1.5, border: '1px solid var(--bd-lo)' },
};

const HEADING_SIZES = { 1: '1.75rem', 2: '1.35rem', 3: '1.1rem', 4: '0.95rem', 5: '0.88rem', 6: '0.82rem' };
const HEADING_WEIGHTS = { 1: 800, 2: 700, 3: 600, 4: 600, 5: 600, 6: 600 };

// ─── Directive block renderers ────────────────────────────────────────────────
function CardBlock({ title, subtitle, children }) {
  return (
    <Box sx={{
      border: '1px solid var(--bd-lo)', borderRadius: 2, p: 2.5,
      my: 0, width: '100%',
      bgcolor: 'var(--bg-card)', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
      display: 'flex', flexDirection: 'column',
    }}>
      {subtitle && <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 1, mb: 0.5 }}>{subtitle}</Typography>}
      {title && <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: 'var(--tx-write)', mb: 1 }}>{title}</Typography>}
      <Box sx={{ ...mdSx, '& p:last-child': { mb: 0 }, flex: 1 }}>{children}</Box>
    </Box>
  );
}

function CardsBlock({ cols = 3, children }) {
  // Filter out blank text nodes / empty <p> tags the parser inserts between cards
  const items = React.Children.toArray(children).filter(child => {
    if (typeof child === 'string') return child.trim().length > 0;
    if (child?.type === 'p' || child?.props?.node?.tagName === 'p') {
      const text = child?.props?.children;
      if (!text || (typeof text === 'string' && !text.trim())) return false;
    }
    return true;
  });
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(cols, 2)}, 1fr)`, md: `repeat(${cols}, 1fr)` },
      gap: 2,
      my: 2,
    }}>
      {items.map((child, i) => <Box key={i} sx={{ display: 'flex' }}>{child}</Box>)}
    </Box>
  );
}

function AccordionBlock({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ border: '1px solid var(--bd-lo)', borderRadius: 2, my: 1.5, overflow: 'hidden' }}>
      <Box onClick={() => setOpen(o => !o)} sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
        cursor: 'pointer', bgcolor: 'var(--bg-card)', userSelect: 'none',
        '&:hover': { bgcolor: 'var(--bg-hover)' },
      }}>
        <ExpandMoreIcon sx={{ fontSize: 18, color: 'var(--tx-dim)', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 0.2s' }} />
        <Typography sx={{ fontWeight: 600, color: 'var(--tx-write)', fontSize: '0.9rem', flex: 1 }}>{title}</Typography>
      </Box>
      <Collapse in={open} timeout={180}>
        <Box sx={{ px: 2.5, py: 2, bgcolor: 'var(--bg-write)', ...mdSx }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

function CalloutBlock({ type = 'info', children }) {
  const styles = {
    info:    { border: '#6366f1', bg: 'rgba(99,102,241,0.08)',  icon: 'ℹ️' },
    warning: { border: '#f97316', bg: 'rgba(249,115,22,0.08)',  icon: '⚠️' },
    tip:     { border: '#10b981', bg: 'rgba(16,185,129,0.08)',  icon: '💡' },
    danger:  { border: '#ef4444', bg: 'rgba(239,68,68,0.08)',   icon: '🚨' },
  };
  const s = styles[type] || styles.info;
  return (
    <Box sx={{ borderLeft: `3px solid ${s.border}`, bgcolor: s.bg, borderRadius: '0 8px 8px 0', px: 2, py: 1.5, my: 1.5, ...mdSx }}>
      <Typography component="span" sx={{ mr: 0.75, fontSize: '0.9rem' }}>{s.icon}</Typography>
      {children}
    </Box>
  );
}

function ColumnsBlock({ children }) {
  // Split children into 2 groups at the --- separator if present
  const childArr = React.Children.toArray(children);
  const sepIdx = childArr.findIndex(c => c?.props?.node?.tagName === 'hr');
  const left = sepIdx >= 0 ? childArr.slice(0, sepIdx) : childArr;
  const right = sepIdx >= 0 ? childArr.slice(sepIdx + 1) : [];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, my: 1.5 }}>
      <Box sx={{ ...mdSx }}>{left}</Box>
      <Box sx={{ ...mdSx }}>{right}</Box>
    </Box>
  );
}

// Custom components map for ReactMarkdown — handles directive divs
function makeComponents() {
  return {
    div({ node, children, ...props }) {
      const dir = props['data-directive'];
      if (!dir) return <div {...props}>{children}</div>;
      const attrs = node?.properties || {};
      if (dir === 'cards')     return <CardsBlock cols={Number(attrs.cols) || 3}>{children}</CardsBlock>;
      if (dir === 'card')      return <Box sx={{ my: 1.5 }}><CardBlock title={attrs.title} subtitle={attrs.subtitle}>{children}</CardBlock></Box>;
      if (dir === 'accordion') return <AccordionBlock title={attrs.title}>{children}</AccordionBlock>;
      if (dir === 'callout')   return <CalloutBlock type={attrs.type}>{children}</CalloutBlock>;
      if (dir === 'columns')   return <ColumnsBlock>{children}</ColumnsBlock>;
      return <div {...props}>{children}</div>;
    },
    h1: ({ children }) => <Typography sx={{ fontSize: HEADING_SIZES[1], fontWeight: HEADING_WEIGHTS[1], color: 'var(--tx-write)', mt: 0, mb: 1, pb: 0.5, borderBottom: '1px solid var(--bd-lo)', lineHeight: 1.3 }}>{children}</Typography>,
    h2: ({ children }) => <Typography sx={{ fontSize: HEADING_SIZES[2], fontWeight: HEADING_WEIGHTS[2], color: 'var(--tx-write)', mt: 2.5, mb: 0.75, lineHeight: 1.3 }}>{children}</Typography>,
    h3: ({ children }) => <Typography sx={{ fontSize: HEADING_SIZES[3], fontWeight: HEADING_WEIGHTS[3], color: 'var(--tx-write)', mt: 1.5, mb: 0.5, lineHeight: 1.3 }}>{children}</Typography>,
    h4: ({ children }) => <Typography sx={{ fontSize: HEADING_SIZES[4], fontWeight: HEADING_WEIGHTS[4], color: 'var(--tx-write)', mt: 1, mb: 0.25, lineHeight: 1.3 }}>{children}</Typography>,
  };
}

// ─── Markdown Preview Component ───────────────────────────────────────────────
const MD_PLUGINS = [remarkGfm, remarkDirective, remarkDirectiveRenderer];
const MD_COMPONENTS = makeComponents();

function MarkdownPreview({ content }) {
  if (!content?.trim()) {
    return (
      <Box sx={{ bgcolor: 'var(--bg-write)', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: 'var(--tx-gh)', fontStyle: 'italic' }}>Nothing to preview yet</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 4, py: 3, height: '100%', overflowY: 'auto', bgcolor: 'var(--bg-write)', ...mdSx }}>
      <ReactMarkdown remarkPlugins={MD_PLUGINS} components={MD_COMPONENTS}>{content}</ReactMarkdown>
    </Box>
  );
}

// ─── Insert Block Menu ────────────────────────────────────────────────────────
const BLOCKS = [
  {
    label: 'Card Grid (3 cols)',
    icon: '▭▭▭',
    snippet: `:::cards\n:::card{title="Title 1" subtitle="LABEL 1"}\nContent here.\n:::\n\n:::card{title="Title 2" subtitle="LABEL 2"}\nContent here.\n:::\n\n:::card{title="Title 3" subtitle="LABEL 3"}\nContent here.\n:::\n:::`,
  },
  {
    label: 'Card (single)',
    icon: '▭',
    snippet: `:::card{title="Title" subtitle="Subtitle"}\nYour content here.\n:::`,
  },
  {
    label: 'Accordion',
    icon: '▼',
    snippet: `:::accordion{title="Click to expand"}\nHidden content here.\n:::`,
  },
  {
    label: 'Callout — Info',
    icon: 'ℹ️',
    snippet: `:::callout{type="info"}\nYour note here.\n:::`,
  },
  {
    label: 'Callout — Warning',
    icon: '⚠️',
    snippet: `:::callout{type="warning"}\nImportant warning here.\n:::`,
  },
  {
    label: 'Callout — Tip',
    icon: '💡',
    snippet: `:::callout{type="tip"}\nHelpful tip here.\n:::`,
  },
  {
    label: 'Callout — Danger',
    icon: '🚨',
    snippet: `:::callout{type="danger"}\nCritical note here.\n:::`,
  },
  {
    label: 'Two Columns',
    icon: '⬛⬛',
    snippet: `:::columns\nLeft column content.\n\n---\n\nRight column content.\n:::`,
  },
];

function InsertBlockMenu({ editorRef, content, onInsert }) {
  const [anchor, setAnchor] = useState(null);

  function insertSnippet(snippet) {
    const ta = editorRef.current;
    const pos = ta ? ta.selectionStart : content.length;
    const before = content.slice(0, pos);
    const after = content.slice(pos);
    const sep = before.length && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
    const updated = before + sep + snippet + '\n\n' + after;
    onInsert(updated);
    setAnchor(null);
    // Restore focus and place cursor after inserted block
    setTimeout(() => {
      if (ta) { ta.focus(); ta.selectionStart = ta.selectionEnd = (before + sep + snippet + '\n\n').length; }
    }, 0);
  }

  return (
    <>
      <Tooltip title="Insert block">
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)}
          sx={{ color: anchor ? '#6366f1' : 'var(--tx-ft)', bgcolor: anchor ? 'var(--bg-card)' : 'transparent', borderRadius: 1, fontSize: '0.78rem', fontWeight: 700, px: 1, gap: 0.4 }}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'inherit', lineHeight: 1 }}>+ Block</Typography>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 2, minWidth: 200, mt: 0.5 } }}>
        <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: '0.65rem', fontWeight: 700, color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.8 }}>Insert block</Typography>
        {BLOCKS.map(b => (
          <MenuItem key={b.label} onClick={() => insertSnippet(b.snippet)}
            sx={{ fontSize: '0.82rem', color: 'var(--tx-lo)', gap: 1.5, py: 0.9, '&:hover': { bgcolor: 'rgba(99,102,241,0.1)', color: 'var(--tx-md)' } }}>
            <Typography sx={{ fontSize: '0.9rem', width: 20, textAlign: 'center' }}>{b.icon}</Typography>
            {b.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ─── Ask AI Panel (selection-based) ───────────────────────────────────────────
function AskAIPanel({ open, onClose, selectedText, entryContent, entryTitle }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMessages([]);
      setInput('');
      setError('');
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open, selectedText]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function handleSend(overrideText) {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    const systemPrompt = `You are a thoughtful AI assistant helping the user explore and understand their journal. The full journal entry is provided as context. The user selected a specific passage and wants to discuss it. Be concise, insightful, and direct.`;

    // Build full message history for the API
    const fullMsgs = [];
    if (entryContent) {
      fullMsgs.push({ role: 'user', content: `Full journal entry${entryTitle ? ` ("${entryTitle}")` : ''}:\n\n${entryContent}` });
      fullMsgs.push({ role: 'assistant', content: 'I have read the full journal entry.' });
    }
    if (messages.length === 0) {
      // First turn: include selected passage + question together
      fullMsgs.push({ role: 'user', content: `Selected passage from the journal:\n> ${selectedText}\n\n${text}` });
    } else {
      // Follow-up: include prior conversation
      fullMsgs.push({ role: 'user', content: `Selected passage from the journal:\n> ${selectedText}` });
      for (const m of messages) fullMsgs.push({ role: m.role, content: m.content });
      fullMsgs.push({ role: 'user', content: text });
    }

    const newMsgs = [...messages, { role: 'user', content: text }];
    setMessages(newMsgs);
    setInput('');
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/api/chat', { messages: fullMsgs, provider, model, system_prompt: systemPrompt });
      setMessages([...newMsgs, { role: 'assistant', content: res.data.reply }]);
    } catch (e) {
      setError(e.response?.data?.error || 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, height: '70vh', display: 'flex', flexDirection: 'column', m: 2 } }}
    >
      {/* Header */}
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <SmartToyIcon sx={{ color: '#6366f1', fontSize: 18 }} />
        <Typography sx={{ fontWeight: 600, color: 'var(--tx-hi)', fontSize: '0.9rem', flex: 1 }}>Ask AI</Typography>
        {/* Provider / model selectors */}
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PROVIDERS[provider]?.color }} />
        <FormControl size="small">
          <Select value={provider} onChange={e => { setProvider(e.target.value); setModel(PROVIDERS[e.target.value].models[0]); }}
            sx={{ fontSize: '0.72rem', color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' }, '& .MuiSvgIcon-root': { color: 'var(--tx-dim)' }, height: 28, minWidth: 100 }}>
            {Object.entries(PROVIDERS).map(([k, p]) => <MenuItem key={k} value={k} sx={{ fontSize: '0.72rem' }}>{p.label}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small">
          <Select value={model} onChange={e => setModel(e.target.value)}
            sx={{ fontSize: '0.72rem', color: 'var(--tx-md)', bgcolor: 'var(--bg-card)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--tx-gh)' }, '& .MuiSvgIcon-root': { color: 'var(--tx-dim)' }, height: 28, minWidth: 120 }}>
            {PROVIDERS[provider].models.map(m => <MenuItem key={m} value={m} sx={{ fontSize: '0.72rem' }}>{m.split('/').pop()}</MenuItem>)}
          </Select>
        </FormControl>
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--tx-dim)', ml: 0.5 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>

      {/* Selected text context */}
      <Box sx={{ px: 2, py: 1, bgcolor: 'var(--bg-base)', borderBottom: '1px solid var(--bg-low)', flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-dim)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Selected passage</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'var(--tx-lo)', fontStyle: 'italic', borderLeft: '2px solid #6366f1', pl: 1.5, lineHeight: 1.5,
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {selectedText}
        </Typography>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {messages.length === 0 && !loading && (
          <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.82rem', textAlign: 'center', mt: 3, fontStyle: 'italic' }}>
            Ask anything about the selected passage
          </Typography>
        )}
        {messages.map((m, i) => (
          <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <Box sx={{ maxWidth: '88%', px: 1.75, py: 1.25, borderRadius: 2,
              bgcolor: m.role === 'user' ? '#312e81' : 'var(--bg-card)',
              color: 'var(--tx-md)', fontSize: '0.85rem', lineHeight: 1.65, whiteSpace: 'pre-wrap', ...mdSx }}>
              {m.role === 'assistant'
                ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                : m.content}
            </Box>
          </Box>
        ))}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Box sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: 'var(--bg-card)', display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {[0,1,2].map(i => <Box key={i} sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#6366f1', animation: 'pulse 1.2s ease-in-out infinite', animationDelay: `${i*0.2}s`, '@keyframes pulse': { '0%,100%': { opacity: 0.3 }, '50%': { opacity: 1 } } }} />)}
            </Box>
          </Box>
        )}
        {error && <Alert severity="error" sx={{ fontSize: '0.78rem' }}>{error}</Alert>}
        <div ref={bottomRef} />
      </Box>

      {/* Input */}
      <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid var(--bd-lo)', display: 'flex', gap: 1, flexShrink: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth size="small"
          placeholder="Ask about the selected text..."
          value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          multiline maxRows={3}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'var(--bg-card)', color: 'var(--tx-md)', borderRadius: 2, fontSize: '0.85rem', '& fieldset': { borderColor: 'var(--tx-gh)' }, '&:hover fieldset': { borderColor: 'var(--tx-ft)' }, '&.Mui-focused fieldset': { borderColor: '#6366f1' } }, '& textarea': { color: 'var(--tx-md)' } }}
        />
        <IconButton onClick={() => handleSend()} disabled={!input.trim() || loading}
          sx={{ bgcolor: '#6366f1', color: '#fff', borderRadius: 2, '&:hover': { bgcolor: '#4f46e5' }, '&.Mui-disabled': { bgcolor: 'var(--bg-card)', color: 'var(--tx-gh)' } }}>
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Dialog>
  );
}

// ─── Main Journal Page ─────────────────────────────────────────────────────────
export default function JournalPage() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigate();
  // Tracker context — set when navigating from an activity node
  const trackerNodeId = searchParams.get('nodeId');
  const trackerNodeTitle = searchParams.get('nodeTitle');
  const trackerEntryId = searchParams.get('entryId');

  const [entries, setEntries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [mode, setMode] = useState('preview'); // 'write' | 'preview'
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [aiPanel, setAiPanel] = useState(null); // { text: selectedText }
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, text }
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = not searching, [] = no results
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimer = useRef(null);
  const [folders, setFolders] = useState([]);
  const [collapsedFolders, setCollapsedFolders] = useState(new Set());
  const [newFolderDialog, setNewFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renameFolderDialog, setRenameFolderDialog] = useState(null); // folder obj
  const [renameFolderName, setRenameFolderName] = useState('');
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null); // { el, entryId }
  const saveTimer = useRef(null);
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const trackerAutoCreated = useRef(false);

  useEffect(() => {
    loadEntries();
  }, []);

  // Reset auto-open guard whenever URL params change (handles SPA back-navigation)
  useEffect(() => {
    trackerAutoCreated.current = false;
  }, [trackerNodeId, trackerEntryId]);

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close, { once: true });
    return () => window.removeEventListener('click', close);
  }, [ctxMenu]);

  // When arriving from tracker: once entries load, auto-select existing note or open new one
  useEffect(() => {
    if (!trackerNodeId || loading || trackerAutoCreated.current) return;
    trackerAutoCreated.current = true;
    // If a specific entryId was passed, open that directly
    if (trackerEntryId) {
      openEntry(trackerEntryId);
      return;
    }
    const existing = entries.find(e => e.tracker_node_id === trackerNodeId);
    if (existing) {
      openEntry(existing.id);
    } else {
      createEntry(trackerNodeId, trackerNodeTitle);
    }
  }, [trackerNodeId, trackerEntryId, loading, entries.length]);

  async function loadEntries() {
    setLoading(true);
    try {
      const [res, fRes] = await Promise.all([api.get('/api/journal'), api.get('/api/journal/folders')]);
      const list = res.data.entries || [];
      setEntries(list);
      setFolders(fRes.data.folders || []);
      if (list.length > 0 && !selectedId) await openEntry(list[0].id);
    } catch {}
    finally { setLoading(false); }
  }

  async function createFolder() {
    if (!newFolderName.trim()) return;
    try {
      const res = await api.post('/api/journal/folders', { name: newFolderName.trim() });
      setFolders(prev => [...prev, res.data.folder]);
      setNewFolderName('');
      setNewFolderDialog(false);
    } catch {}
  }

  async function renameFolder() {
    if (!renameFolderDialog || !renameFolderName.trim()) return;
    try {
      await api.put(`/api/journal/folders/${renameFolderDialog.id}`, { name: renameFolderName.trim() });
      setFolders(prev => prev.map(f => f.id === renameFolderDialog.id ? { ...f, name: renameFolderName.trim() } : f));
      setRenameFolderDialog(null);
    } catch {}
  }

  async function deleteFolder(folderId) {
    try {
      await api.delete(`/api/journal/folders/${folderId}`);
      setFolders(prev => prev.filter(f => f.id !== folderId));
      setEntries(prev => prev.map(e => e.folder_id === folderId ? { ...e, folder_id: undefined } : e));
    } catch {}
  }

  async function moveEntryToFolder(entryId, folderId) {
    try {
      await api.patch(`/api/journal/${entryId}`, { folder_id: folderId || null });
      setEntries(prev => prev.map(e => e.id === entryId ? { ...e, folder_id: folderId || undefined } : e));
    } catch {}
    setFolderMenuAnchor(null);
  }

  const toggleFolder = id => setCollapsedFolders(prev => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });

  function handleSearchChange(val) {
    setSearchQuery(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!val.trim()) { setSearchResults(null); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get(`/api/journal/search?q=${encodeURIComponent(val.trim())}`);
        setSearchResults(res.data.entries || []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 400);
  }

  async function openEntry(id) {
    try {
      const res = await api.get(`/api/journal/${id}`);
      const e = res.data.entry;
      const headingMatch = (e.content || '').match(/^#\s+(.+)/m);
      const displayTitle = headingMatch ? headingMatch[1].trim() : (e.title || '');
      setSelectedId(e.id);
      setContent(e.content || '');
      setTitle(displayTitle);
      if (isMobile) setSidebarOpen(false);
      setMode('write');
      setDirty(false);
      setAiPanel(null);
      // Sync sidebar title if it differs from what the heading says
      if (displayTitle !== e.title) {
        setEntries(prev => prev.map(en => en.id === e.id ? { ...en, title: displayTitle } : en));
        api.put(`/api/journal/${e.id}`, { title: displayTitle, content: e.content }).catch(() => {});
      }
    } catch {}
  }

  async function createEntry(nodeId, nodeTitle) {
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-SG', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    let defaultTitle, defaultContent;
    if (nodeId && nodeTitle) {
      defaultTitle = `${nodeTitle} — ${now.toLocaleDateString('en-SG', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      defaultContent = `# ${defaultTitle}\n\n## Attendees\n- \n\n## Agenda\n- \n\n## Notes\n\n\n## Action Items\n- [ ] \n`;
    } else {
      defaultTitle = dateStr;
      defaultContent = '';
    }
    try {
      const body = { title: defaultTitle, content: defaultContent };
      if (nodeId) body.tracker_node_id = nodeId;
      const res = await api.post('/api/journal', body);
      const e = res.data.entry;
      const preview = defaultContent.replace(/[#*`_>\-\[\]]/g, '').trim().slice(0, 120);
      setEntries(prev => [{ id: e.id, title: e.title, preview, created_at: e.created_at, updated_at: e.updated_at, tracker_node_id: nodeId || undefined }, ...prev]);
      setSelectedId(e.id);
      setContent(defaultContent);
      setTitle(defaultTitle);
      setMode('write');
      setDirty(false);
      setAiPanel(null);
      if (isMobile) setSidebarOpen(false);
    } catch {}
  }

  // Auto-save with 1.5s debounce
  const autoSave = useCallback((id, t, c) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/api/journal/${id}`, { title: t, content: c });
        setEntries(prev => prev.map(e => e.id === id
          ? { ...e, title: t, preview: c.replace(/[#*`_>\-]/g, '').trim().slice(0, 120), updated_at: new Date().toISOString() }
          : e));
        setDirty(false);
      } catch {}
      finally { setSaving(false); }
    }, 1500);
  }, []);

  function handleContentChange(val) {
    setContent(val);
    setDirty(true);
    // Extract first # heading as the title
    const headingMatch = val.match(/^#\s+(.+)/m);
    const derivedTitle = headingMatch ? headingMatch[1].trim() : title;
    if (headingMatch && derivedTitle !== title) setTitle(derivedTitle);
    if (selectedId) autoSave(selectedId, derivedTitle, val);
  }

  function handleTitleChange(val) {
    setTitle(val);
    setDirty(true);
    if (selectedId) autoSave(selectedId, val, content);
  }

  function resizeImage(file, maxWidth = 1200, maxHeight = 900, quality = 0.82) {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width <= maxWidth && height <= maxHeight) { resolve(file); return; }
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' })), 'image/jpeg', quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  }

  async function uploadImageFile(file, cursorStart, cursorEnd) {
    setUploading(true);
    const placeholder = `![uploading ${file.name}…]()`;
    // Insert placeholder at cursor
    const ta = editorRef.current;
    const before = content.slice(0, cursorStart);
    const after = content.slice(cursorEnd ?? cursorStart);
    const withPlaceholder = before + placeholder + after;
    handleContentChange(withPlaceholder);
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append('file', resized);
      const res = await api.post('/api/journal/images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const mdTag = `![${file.name}](${res.data.url})`;
      // Replace placeholder with real tag
      setContent(prev => {
        const updated = prev.replace(placeholder, mdTag);
        if (ta) setTimeout(() => { ta.selectionStart = ta.selectionEnd = before.length + mdTag.length; ta.focus(); }, 0);
        if (selectedId) autoSave(selectedId, title, updated);
        return updated;
      });
    } catch (err) {
      // Remove placeholder on failure
      setContent(prev => prev.replace(placeholder, ''));
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
    }
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ta = editorRef.current;
    await uploadImageFile(file, ta?.selectionStart ?? content.length, ta?.selectionEnd ?? content.length);
    e.target.value = '';
  }

  async function handleEditorPaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find(item => item.type.startsWith('image/'));
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (!file) return;
    const ta = editorRef.current;
    const namedFile = new File([file], `pasted-image-${Date.now()}.png`, { type: file.type });
    await uploadImageFile(namedFile, ta?.selectionStart ?? content.length, ta?.selectionEnd ?? content.length);
  }

  async function deleteEntry(id) {
    try {
      await api.delete(`/api/journal/${id}`);
      const remaining = entries.filter(e => e.id !== id);
      setEntries(remaining);
      if (selectedId === id) {
        if (remaining.length > 0) {
          await openEntry(remaining[0].id);
        } else {
          setSelectedId(null);
          setContent('');
          setTitle('');
        }
      }
    } catch {}
    setDeleteDialog(null);
  }

  const selectedEntry = entries.find(e => e.id === selectedId);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 52px)', bgcolor: 'var(--bg-base)', overflow: 'hidden' }}>

      {/* ── Left sidebar: entry list ─────────────────────────────────────────── */}
      {isMobile && sidebarOpen && (
        <Box onClick={() => setSidebarOpen(false)} sx={{ position: 'fixed', inset: 0, zIndex: 1199, bgcolor: 'rgba(0,0,0,0.5)' }} />
      )}
      <Box sx={{
        ...(isMobile ? {
          position: 'fixed', left: sidebarOpen ? 0 : -300, top: 52, bottom: 0, zIndex: 1200, transition: 'left 0.25s ease', width: 280,
        } : {
          width: sidebarOpen ? 280 : 0, minWidth: sidebarOpen ? 280 : 0, overflow: 'hidden',
          transition: 'width 0.22s ease, min-width 0.22s ease', flexShrink: 0,
          borderRight: sidebarOpen ? '1px solid var(--bd-lo)' : 'none',
        }),
        display: 'flex', flexDirection: 'column', bgcolor: 'var(--bg-surf)',
      }}>
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontWeight: 700, color: 'var(--tx-hi)', flex: 1, fontSize: '1rem', letterSpacing: 0.3 }}>
            {trackerNodeId ? 'Meeting Notes' : 'Journal'}
          </Typography>
          {!trackerNodeId && (
            <Tooltip title="New folder">
              <IconButton size="small" onClick={() => { setNewFolderName(''); setNewFolderDialog(true); }}
                sx={{ color: 'var(--tx-dim)', '&:hover': { color: '#6366f1' }, width: 26, height: 26 }}>
                <CreateNewFolderIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="New entry">
            <IconButton size="small" onClick={() => createEntry(trackerNodeId || undefined, trackerNodeTitle || undefined)}
              sx={{ bgcolor: '#6366f1', color: '#fff', '&:hover': { bgcolor: '#4f46e5' }, width: 28, height: 28 }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={() => setSidebarOpen(false)} sx={{ color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-md)' }, width: 26, height: 26 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Tracker context banner */}
        {trackerNodeId && (
          <Box sx={{ px: 1.5, py: 0.8, bgcolor: 'rgba(99,102,241,0.1)', borderBottom: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AccountTreeIcon sx={{ fontSize: 13, color: '#818cf8', flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.7rem', color: '#818cf8', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {trackerNodeTitle}
            </Typography>
            <Tooltip title="Back to Tracker">
              <IconButton size="small" onClick={() => navigateTo('/tracker')}
                sx={{ color: '#818cf8', '&:hover': { color: '#c4b5fd' }, p: 0.25 }}>
                <AccountTreeIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Show all journal entries">
              <IconButton size="small" onClick={() => navigateTo('/journal')}
                sx={{ color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-md)' }, p: 0.25 }}>
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Search bar */}
        {!trackerNodeId && (
          <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid var(--bd-lo)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'var(--bg-card)', borderRadius: 1.5, px: 1.2, py: 0.5, gap: 0.5, border: '1px solid var(--bd-lo)' }}>
              <Typography sx={{ fontSize: 13, color: 'var(--tx-dim)', flexShrink: 0 }}>🔍</Typography>
              <Box
                component="input"
                value={searchQuery}
                onChange={e => handleSearchChange(e.target.value)}
                placeholder="Semantic search…"
                sx={{
                  flex: 1, border: 'none', outline: 'none', bgcolor: 'transparent',
                  fontSize: '0.78rem', color: 'var(--tx-write)', '::placeholder': { color: 'var(--tx-gh)' },
                  fontFamily: 'inherit',
                }}
              />
              {searchQuery && (
                <IconButton size="small" onClick={() => { setSearchQuery(''); setSearchResults(null); }}
                  sx={{ p: 0.2, color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-lo)' } }}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              )}
              {searchLoading && <CircularProgress size={12} sx={{ color: '#6366f1', flexShrink: 0 }} />}
            </Box>
          </Box>
        )}

        {/* Entry list */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} sx={{ color: '#6366f1' }} /></Box>
          ) : searchResults !== null ? (() => {
            if (searchResults.length === 0) return (
              <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
                <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.82rem' }}>No matching entries</Typography>
              </Box>
            );
            return searchResults.map(e => (
              <Box key={e.id} sx={{
                borderBottom: '1px solid var(--bg-low)',
                bgcolor: selectedId === e.id ? 'var(--bg-card)' : 'transparent',
                borderLeft: selectedId === e.id ? '3px solid #6366f1' : '3px solid transparent',
                transition: 'all 0.15s', '&:hover': { bgcolor: 'var(--bg-hover)' },
                px: 1.5, py: 1.1, cursor: 'pointer',
              }} onClick={() => openEntry(e.id)}>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: selectedId === e.id ? 'var(--tx-md)' : 'var(--tx-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {e.title || 'Untitled'}
                </Typography>
                {e.preview && (
                  <Typography sx={{ fontSize: '0.7rem', color: 'var(--tx-ft)', mt: 0.3, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {e.preview}
                  </Typography>
                )}
              </Box>
            ));
          })() : (() => {
            const visibleEntries = entries.filter(e => !trackerNodeId || e.tracker_node_id === trackerNodeId);
            if (visibleEntries.length === 0) return (
              <Box sx={{ textAlign: 'center', mt: 6, px: 2 }}>
                <Typography sx={{ color: 'var(--tx-gh)', fontSize: '0.85rem' }}>No entries yet</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => createEntry(trackerNodeId || undefined, trackerNodeTitle || undefined)}
                  sx={{ mt: 1, color: '#6366f1', textTransform: 'none' }}>Create first entry</Button>
              </Box>
            );

            const EntryRow = ({ e }) => {
              const isExpanded = expandedEntries.has(e.id);
              return (
                <Box sx={{
                  borderBottom: '1px solid var(--bg-low)',
                  bgcolor: selectedId === e.id ? 'var(--bg-card)' : 'transparent',
                  borderLeft: selectedId === e.id ? '3px solid #6366f1' : '3px solid transparent',
                  transition: 'all 0.15s', '&:hover': { bgcolor: 'var(--bg-hover)' },
                  '&:hover .entry-actions': { opacity: 1 },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1.1, cursor: 'pointer', gap: 0.25 }}
                    onClick={() => openEntry(e.id)}>
                    <Typography sx={{ flex: 1, fontSize: '0.8rem', fontWeight: 600, color: selectedId === e.id ? 'var(--tx-md)' : 'var(--tx-lo)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {e.title || 'Untitled'}
                    </Typography>
                    <Stack direction="row" className="entry-actions" sx={{ opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }}>
                      {!trackerNodeId && (
                        <Tooltip title="Move to folder">
                          <IconButton size="small" onClick={ev => { ev.stopPropagation(); setFolderMenuAnchor({ el: ev.currentTarget, entryId: e.id }); }}
                            sx={{ p: 0.2, color: '#818cf8', '&:hover': { color: '#a5b4fc' } }}>
                            <DriveFileMoveOutlinedIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small"
                        onClick={ev => { ev.stopPropagation(); setExpandedEntries(prev => { const s = new Set(prev); s.has(e.id) ? s.delete(e.id) : s.add(e.id); return s; }); }}
                        sx={{ p: 0.2, color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-lo)' } }}>
                        <ExpandMoreIcon sx={{ fontSize: 14, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                      </IconButton>
                      <IconButton size="small" onClick={ev => { ev.stopPropagation(); setDeleteDialog(e.id); }}
                        sx={{ p: 0.2, color: 'var(--tx-ft)', '&:hover': { color: '#ef4444' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </Stack>
                  </Box>
                  <Collapse in={isExpanded} timeout={150}>
                    <Box sx={{ px: 2, pb: 1.25 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-ft)', mb: 0.5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                        {e.preview || 'Empty entry'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: 'var(--tx-gh)' }}>{timeAgo(e.updated_at)}</Typography>
                    </Box>
                  </Collapse>
                </Box>
              );
            };

            if (trackerNodeId) return visibleEntries.map(e => <EntryRow key={e.id} e={e} />);

            const uncategorized = visibleEntries.filter(e => !e.folder_id);
            return (
              <>
                {folders.map(folder => {
                  const folderEntries = visibleEntries.filter(e => e.folder_id === folder.id);
                  const isCollapsed = collapsedFolders.has(folder.id);
                  return (
                    <Box key={folder.id}>
                      {/* Folder header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, cursor: 'pointer', gap: 0.5,
                        '&:hover': { bgcolor: 'var(--bg-hover)' }, '&:hover .folder-actions': { opacity: 1 } }}
                        onClick={() => toggleFolder(folder.id)}>
                        {isCollapsed
                          ? <FolderIcon sx={{ fontSize: 14, color: '#6366f1', flexShrink: 0 }} />
                          : <FolderOpenIcon sx={{ fontSize: 14, color: '#818cf8', flexShrink: 0 }} />}
                        <Typography sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 700, color: 'var(--tx-md)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {folder.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)', flexShrink: 0 }}>{folderEntries.length}</Typography>
                        <Stack direction="row" className="folder-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
                          <IconButton size="small" onClick={ev => { ev.stopPropagation(); setRenameFolderName(folder.name); setRenameFolderDialog(folder); }}
                            sx={{ p: 0.15, color: 'var(--tx-dim)', '&:hover': { color: 'var(--tx-md)' } }}>
                            <EditIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                          <IconButton size="small" onClick={ev => { ev.stopPropagation(); deleteFolder(folder.id); }}
                            sx={{ p: 0.15, color: 'var(--tx-dim)', '&:hover': { color: '#ef4444' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 11 }} />
                          </IconButton>
                        </Stack>
                        <ExpandMoreIcon sx={{ fontSize: 13, color: 'var(--tx-dim)', transform: isCollapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }} />
                      </Box>
                      <Collapse in={!isCollapsed} timeout={160}>
                        <Box sx={{ pl: 1.5, borderLeft: '2px solid #6366f133' }}>
                          {folderEntries.length === 0
                            ? <Typography sx={{ px: 2, py: 0.75, fontSize: '0.72rem', color: 'var(--tx-gh)', fontStyle: 'italic' }}>Empty folder</Typography>
                            : folderEntries.map(e => <EntryRow key={e.id} e={e} />)}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
                {/* Uncategorized */}
                {uncategorized.length > 0 && (
                  <Box>
                    {folders.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, cursor: 'pointer', gap: 0.5, '&:hover': { bgcolor: 'var(--bg-hover)' } }}
                        onClick={() => toggleFolder('__uncategorized__')}>
                        <FolderIcon sx={{ fontSize: 14, color: 'var(--tx-dim)', flexShrink: 0 }} />
                        <Typography sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 700, color: 'var(--tx-dim)' }}>Uncategorized</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'var(--tx-gh)' }}>{uncategorized.length}</Typography>
                        <ExpandMoreIcon sx={{ fontSize: 13, color: 'var(--tx-dim)', transform: collapsedFolders.has('__uncategorized__') ? 'rotate(-90deg)' : 'none', transition: 'transform 0.2s' }} />
                      </Box>
                    )}
                    <Collapse in={!collapsedFolders.has('__uncategorized__')} timeout={160}>
                      <Box sx={folders.length > 0 ? { pl: 1.5, borderLeft: '2px solid var(--bd-lo)' } : {}}>
                        {uncategorized.map(e => <EntryRow key={e.id} e={e} />)}
                      </Box>
                    </Collapse>
                  </Box>
                )}
              </>
            );
          })()}
        </Box>

        {/* Folder move menu — only show outside search mode */}
        <Menu anchorEl={folderMenuAnchor?.el} open={!!folderMenuAnchor} onClose={() => setFolderMenuAnchor(null)}
          PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 2, minWidth: 180 } }}>
          <Typography sx={{ px: 2, py: 0.75, fontSize: '0.68rem', color: 'var(--tx-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>Move to folder</Typography>
          {folders.map(f => (
            <MenuItem key={f.id} onClick={() => moveEntryToFolder(folderMenuAnchor.entryId, f.id)}
              sx={{ fontSize: '0.82rem', color: 'var(--tx-lo)', gap: 1, '&:hover': { bgcolor: 'rgba(99,102,241,0.12)' } }}>
              <FolderIcon sx={{ fontSize: 14, color: '#6366f1' }} />{f.name}
            </MenuItem>
          ))}
          <Divider sx={{ borderColor: 'var(--bd-lo)', my: 0.5 }} />
          <MenuItem onClick={() => moveEntryToFolder(folderMenuAnchor.entryId, null)}
            sx={{ fontSize: '0.82rem', color: 'var(--tx-gh)', gap: 1, '&:hover': { bgcolor: 'var(--bg-hover)' } }}>
            <FolderIcon sx={{ fontSize: 14, color: 'var(--tx-dim)' }} />Uncategorized
          </MenuItem>
        </Menu>
      </Box>

      {/* ── Editor area ─────────────────────────────────────────────────────── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {selectedId ? (
          <>
            {/* Toolbar */}
            <Box sx={{ px: { xs: 1.5, sm: 3 }, py: 1.2, borderBottom: '1px solid var(--bd-lo)', display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 }, bgcolor: 'var(--bg-surf)' }}>
              {/* Sidebar toggle */}
              <Tooltip title={sidebarOpen ? 'Hide sidebar' : 'Show sidebar'}>
                <IconButton size="small" onClick={() => setSidebarOpen(o => !o)}
                  sx={{ color: sidebarOpen ? '#818cf8' : 'var(--tx-ft)', bgcolor: sidebarOpen ? 'var(--bg-card)' : 'transparent', borderRadius: 1, mr: 0.5 }}>
                  {sidebarOpen ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              {/* Title */}
              <input
                value={title}
                onChange={e => handleTitleChange(e.target.value)}
                placeholder="Entry title..."
                style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--tx-hi)', fontFamily: 'inherit', fontSize: '1rem', fontWeight: 600 }}
              />
              {/* Save status */}
              <Typography sx={{ fontSize: '0.7rem', color: saving ? '#6366f1' : dirty ? '#fbbf24' : 'var(--tx-gh)', minWidth: 60, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                {saving ? 'Saving…' : dirty ? 'Unsaved' : 'Saved'}
              </Typography>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'flex' }, borderColor: 'var(--bg-card)', mx: 0.5 }} />
              {/* Write/Preview toggle */}
              <Tooltip title="Edit mode">
                <IconButton size="small" onClick={() => setMode('write')} sx={{ color: mode === 'write' ? '#6366f1' : 'var(--tx-ft)', bgcolor: mode === 'write' ? 'var(--bg-card)' : 'transparent', borderRadius: 1, flexShrink: 0 }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Preview mode">
                <IconButton size="small" onClick={() => setMode('preview')} sx={{ color: mode === 'preview' ? '#6366f1' : 'var(--tx-ft)', bgcolor: mode === 'preview' ? 'var(--bg-card)' : 'transparent', borderRadius: 1, flexShrink: 0 }}>
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Split view">
                <IconButton size="small" onClick={() => setMode('split')} sx={{ color: mode === 'split' ? '#6366f1' : 'var(--tx-ft)', bgcolor: mode === 'split' ? 'var(--bg-card)' : 'transparent', borderRadius: 1, flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' } }}>
                  <VerticalSplitIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'var(--bg-card)', mx: 0.5 }} />
              {/* Insert block menu */}
              {(mode === 'write' || mode === 'split') && (
                <InsertBlockMenu editorRef={editorRef} content={content} onInsert={handleContentChange} />
              )}
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'var(--bg-card)', mx: 0.5 }} />
              {/* Image upload */}
              <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
              <Tooltip title="Insert image">
                <span>
                  <IconButton size="small" onClick={() => imageInputRef.current?.click()} disabled={uploading}
                    sx={{ color: uploading ? '#6366f1' : 'var(--tx-ft)', borderRadius: 1 }}
                  >
                    <ImageIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Date line + tracker back button */}
            <Box sx={{ px: 3, py: 0.8, bgcolor: 'var(--bg-base)', borderBottom: '1px solid var(--bg-low)', display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedEntry?.tracker_node_id ? (
                <Button
                  size="small"
                  startIcon={<AccountTreeIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => navigateTo('/tracker')}
                  sx={{ fontSize: '0.68rem', color: '#818cf8', textTransform: 'none', px: 1, py: 0.25, borderRadius: 1.5,
                    bgcolor: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.2)', color: '#c4b5fd' }, minWidth: 0 }}
                >
                  ← Back to Tracker
                </Button>
              ) : (
                <Typography sx={{ fontSize: '0.72rem', color: 'var(--tx-gh)' }}>
                  {selectedEntry ? formatDate(selectedEntry.created_at) : ''}
                </Typography>
              )}
              <Box sx={{ flex: 1 }} />
              {selectedEntry?.tracker_node_id && (
                <Typography sx={{ fontSize: '0.68rem', color: 'var(--tx-gh)' }}>
                  {selectedEntry ? formatDate(selectedEntry.created_at) : ''}
                </Typography>
              )}
            </Box>

            {/* Editor / Preview */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'row' }}>
              {/* Editor pane — shown in write + split */}
              {(mode === 'write' || mode === 'split') && (
                <textarea
                  ref={editorRef}
                  value={content}
                  onChange={e => handleContentChange(e.target.value)}
                  onPaste={handleEditorPaste}
                  placeholder={"# What's on your mind?\n\nStart writing in markdown..."}
                  style={{
                    flex: 1, width: '100%', height: '100%', padding: '20px 24px',
                    background: 'var(--bg-write)', color: 'var(--tx-write)',
                    border: 'none', outline: 'none', resize: 'none',
                    borderRight: mode === 'split' ? '1px solid var(--bd-lo)' : 'none',
                    fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",
                    fontSize: '0.92rem', lineHeight: 1.8, boxSizing: 'border-box',
                  }}
                />
              )}
              {/* Preview pane — shown in preview + split */}
              {(mode === 'preview' || mode === 'split') && (
                <Box
                  sx={{ flex: 1, overflow: 'auto', height: '100%' }}
                  onContextMenu={e => {
                    const sel = window.getSelection()?.toString().trim();
                    if (sel && sel.length > 3) {
                      e.preventDefault();
                      setCtxMenu({ x: e.clientX, y: e.clientY, text: sel });
                    }
                  }}
                >
                  <MarkdownPreview content={content} />
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, position: 'relative' }}>
            {!sidebarOpen && (
              <Tooltip title="Show sidebar">
                <IconButton size="small" onClick={() => setSidebarOpen(true)}
                  sx={{ position: 'absolute', top: 12, left: 12, color: 'var(--tx-ft)', '&:hover': { color: 'var(--tx-md)' } }}>
                  <MenuIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Typography sx={{ color: 'var(--tx-gh)', fontSize: '1rem' }}>No entry selected</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={createEntry} sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}>
              New Entry
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Context menu for AI (right-click on selected preview text) ──────── */}
      {ctxMenu && (
        <Box
          sx={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 9999,
            bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 1.5,
            boxShadow: '0 4px 20px rgba(0,0,0,0.35)', py: 0.5, minWidth: 180 }}
          onMouseLeave={() => setCtxMenu(null)}
        >
          <Box
            onClick={() => { setAiPanel({ text: ctxMenu.text }); setCtxMenu(null); }}
            sx={{ px: 2, py: 0.9, display: 'flex', alignItems: 'center', gap: 1.5,
              cursor: 'pointer', '&:hover': { bgcolor: 'rgba(99,102,241,0.12)' } }}
          >
            <SmartToyIcon sx={{ fontSize: 15, color: '#6366f1' }} />
            <Typography sx={{ fontSize: '0.82rem', color: 'var(--tx-md)' }}>Ask AI about this</Typography>
          </Box>
        </Box>
      )}

      {/* ── Ask AI Panel ─────────────────────────────────────────────────────── */}
      <AskAIPanel
        open={!!aiPanel}
        onClose={() => setAiPanel(null)}
        selectedText={aiPanel?.text || ''}
        entryContent={content}
        entryTitle={title}
      />

      {/* Delete confirm dialog */}
      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3 } }}>
        <DialogTitle sx={{ color: 'var(--tx-hi)', fontSize: '1rem' }}>Delete Entry?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'var(--tx-lo)', fontSize: '0.875rem' }}>This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialog(null)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={() => deleteEntry(deleteDialog)} variant="contained" sx={{ bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, textTransform: 'none', borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* New folder dialog */}
      <Dialog open={newFolderDialog} onClose={() => setNewFolderDialog(false)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, minWidth: 320 } }}>
        <DialogTitle sx={{ color: 'var(--tx-hi)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreateNewFolderIcon sx={{ color: '#6366f1', fontSize: 20 }} /> New Folder
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Folder name" value={newFolderName}
            onChange={e => setNewFolderName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && createFolder()}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: 'var(--tx-hi)' }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewFolderDialog(false)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={createFolder} variant="contained" disabled={!newFolderName.trim()}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={!!renameFolderDialog} onClose={() => setRenameFolderDialog(null)}
        PaperProps={{ sx: { bgcolor: 'var(--bg-surf)', border: '1px solid var(--bd-lo)', borderRadius: 3, minWidth: 320 } }}>
        <DialogTitle sx={{ color: 'var(--tx-hi)', fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 1 }}>
          <FolderIcon sx={{ color: '#6366f1', fontSize: 20 }} /> Rename Folder
        </DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" label="Folder name" value={renameFolderName}
            onChange={e => setRenameFolderName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && renameFolder()}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: 'var(--tx-hi)' }, '& .MuiInputLabel-root': { color: 'var(--tx-dim)' } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenameFolderDialog(null)} sx={{ color: 'var(--tx-dim)', textTransform: 'none' }}>Cancel</Button>
          <Button onClick={renameFolder} variant="contained" disabled={!renameFolderName.trim()}
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' }, textTransform: 'none', borderRadius: 2 }}>Rename</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
